import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import './LoginPage.css';

function AuthCallback() {
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const { showToast } = useToast();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get token from URL hash or query params
        const hash = window.location.hash.substring(1);
        const search = window.location.search.substring(1);
        const params = new URLSearchParams(hash || search);

        const token = params.get('token') || params.get('access_token');
        const errorParam = params.get('error');
        const errorDesc = params.get('error_description');

        if (errorParam) {
          throw new Error(errorDesc || errorParam || 'Authentication failed');
        }

        if (!token) {
          throw new Error('No authentication token received');
        }

        // Store the token in localStorage
        localStorage.setItem('auth_token', token);

        // Update auth context and WAIT for it to complete
        if (setToken) {
          await setToken(token);
        }

        setStatus('success');
        showToast('Successfully signed in!', 'success');

        // Redirect to dashboard immediately after token is set
        navigate('/', { replace: true });

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setError(err.message);
        showToast(err.message || 'Authentication failed', 'error');
      }
    };

    processCallback();
  }, [navigate, setToken, showToast]);

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo auth1-logo">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <rect width="64" height="64" rx="16" fill="url(#auth1-gradient)"/>
              <path d="M32 16L44 28L32 40L20 28L32 16Z" fill="white" fillOpacity="0.9"/>
              <path d="M32 24L38 30L32 36L26 30L32 24Z" fill="white"/>
              <path d="M20 36L32 48L44 36" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <defs>
                <linearGradient id="auth1-gradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#6366f1"/>
                  <stop offset="1" stopColor="#8b5cf6"/>
                </linearGradient>
              </defs>
            </svg>
          </div>

          {status === 'processing' && (
            <>
              <h1 className="login-title">Signing you in...</h1>
              <p className="login-subtitle">Please wait while we complete authentication</p>
              <div className="auth-spinner-container">
                <div className="auth-spinner"></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <h1 className="login-title">Welcome!</h1>
              <p className="login-subtitle">Redirecting to dashboard...</p>
              <div className="auth-success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="login-title">Authentication Failed</h1>
              <p className="login-subtitle">{error || 'Something went wrong'}</p>
              <div className="auth-error-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <button
                className="auth1-login-button"
                onClick={() => navigate('/login')}
                style={{ marginTop: '24px' }}
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>

      <div className="login-bg-decoration">
        <div className="decoration-circle decoration-1"></div>
        <div className="decoration-circle decoration-2"></div>
        <div className="decoration-circle decoration-3"></div>
      </div>
    </div>
  );
}

export default AuthCallback;
